import { authFetch, getAuthHeaders } from '../lib/authFetch';

const getBaseUrl = (url: string) => url.startsWith('http') ? url : `https://${url}`;

const API_URL = import.meta.env.VITE_API_BASE_URL
    ? `${getBaseUrl(import.meta.env.VITE_API_BASE_URL)}/api/wallet`
    : 'https://stingray-app-2pifp.ondigitalocean.app/api/wallet';

// ---------------------------------------------------------------------------
// User-scoped localStorage helpers
// Reads the logged-in user's _id from the 'userInfo' entry and prefixes all
// per-user cache keys so that different accounts never collide.
// ---------------------------------------------------------------------------
const getUserId = (): string => {
    try {
        const raw = localStorage.getItem('userInfo');
        if (raw) {
            const parsed = JSON.parse(raw);
            return parsed._id || '';
        }
    } catch {}
    return '';
};

const userKey = (base: string): string => {
    const uid = getUserId();
    return uid ? `${uid}_${base}` : base;
};

// ---------------------------------------------------------------------------
// Local balance ledger – persists across page navigations
// All simulated buy/sell/transfer/trade operations write here so the balance
// is always correct even when the backend hasn't been updated yet.
// ---------------------------------------------------------------------------

export const getLocalBalanceAdjustment = (): number => {
    try { return parseFloat(localStorage.getItem(userKey('dw_local_bal_adj')) || '0') || 0; } catch { return 0; }
};

export const applyLocalBalanceChange = (delta: number): void => {
    try {
        const current = getLocalBalanceAdjustment();
        localStorage.setItem(userKey('dw_local_bal_adj'), (current + delta).toFixed(8));
    } catch { /* ignore */ }
};

export const resetLocalBalanceAdjustment = (): void => {
    try { localStorage.removeItem(userKey('dw_local_bal_adj')); } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// One-time migration (v2): the old code incorrectly wrote futures trade margins
// into dw_local_bal_adj, inflating the displayed spot balance. The correct value
// of this key should only reflect spot↔futures transfers — i.e. it should equal
// -(futuresBalance) so that spot shown = backend_balance - amount_in_futures.
// Run once on load; subsequent operations keep it correct automatically.
// ---------------------------------------------------------------------------

export const runBalanceAdjMigrationIfNeeded = (): void => {
    try {
        if (localStorage.getItem(userKey('dw_adj_v2_migrated'))) return; // already done
        const futuresBal = parseFloat(localStorage.getItem(userKey('dw_futures_balance')) || '0') || 0;
        // Only migrate if there IS a futures balance; if 0 the adj should also be 0
        if (futuresBal > 0) {
            localStorage.setItem(userKey('dw_local_bal_adj'), (-futuresBal).toFixed(8));
        } else {
            localStorage.removeItem(userKey('dw_local_bal_adj'));
        }
        localStorage.setItem(userKey('dw_adj_v2_migrated'), '1');
    } catch { /* ignore */ }
};
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Cached (spot) balance – persists optimistic balance so the UI shows the
// latest value even before the next backend fetch.
// ---------------------------------------------------------------------------


export const getCachedBalance = (): number => {
    try { return parseFloat(localStorage.getItem(userKey('dw_cached_balance')) || '0') || 0; } catch { return 0; }
};

export const setCachedBalance = (balance: number): void => {
    try { localStorage.setItem(userKey('dw_cached_balance'), balance.toFixed(8)); } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Spot holdings – coin → amount mapping stored in localStorage
// ---------------------------------------------------------------------------


export const getSpotHoldings = (): Record<string, number> => {
    try {
        const raw = localStorage.getItem(userKey('dw_spot_holdings'));
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
};

// Returns a Record<symbol, epoch-ms> for when each coin was last bought
export const getSpotTimestamps = (): Record<string, number> => {
    try { return JSON.parse(localStorage.getItem(userKey('dw_spot_timestamps')) || '{}'); } catch { return {}; }
};

const saveSpotHoldings = (holdings: Record<string, number>): void => {
    try { localStorage.setItem(userKey('dw_spot_holdings'), JSON.stringify(holdings)); } catch { /* ignore */ }
};

export const getSpotHolding = (coin: string): number => {
    return getSpotHoldings()[coin.toUpperCase()] || 0;
};

export const addSpotHolding = (coin: string, amount: number, usdtCost?: number): void => {
    const holdings = getSpotHoldings();
    const key = coin.toUpperCase();
    holdings[key] = parseFloat(((holdings[key] || 0) + amount).toFixed(8));
    saveSpotHoldings(holdings);
    // Track last-bought timestamp for sorting
    try {
        const tsKey = userKey('dw_spot_timestamps');
        const existing = JSON.parse(localStorage.getItem(tsKey) || '{}');
        existing[key] = Date.now();
        localStorage.setItem(tsKey, JSON.stringify(existing));
    } catch { /* ignore */ }
    // Track how much USDT was invested in this coin
    if (usdtCost && usdtCost > 0) {
        try {
            const costKey = userKey('dw_cost_basis');
            const existing = JSON.parse(localStorage.getItem(costKey) || '{}');
            existing[key] = parseFloat(((existing[key] || 0) + usdtCost).toFixed(4));
            localStorage.setItem(costKey, JSON.stringify(existing));
        } catch { /* ignore */ }
    }
};

// Returns total USDT invested in each coin
export const getSpotCostBasis = (): Record<string, number> => {
    try { return JSON.parse(localStorage.getItem(userKey('dw_cost_basis')) || '{}'); } catch { return {}; }
};

// ---------------------------------------------------------------------------
// Last known prices — persists the most recent successful price fetch
// so that USD values still display even when the price API is down.
// Keys are SYMBOLUSDT (e.g. "XRPUSDT"), values are USD price numbers.
// ---------------------------------------------------------------------------
const LAST_PRICES_KEY = 'dw_last_prices';

export const getLastKnownPrices = (): Record<string, number> => {
    try { return JSON.parse(localStorage.getItem(LAST_PRICES_KEY) || '{}'); } catch { return {}; }
};

export const saveLastKnownPrices = (prices: Record<string, number>): void => {
    try {
        // Merge with existing so we never lose a coin's last price
        const existing = getLastKnownPrices();
        const merged = { ...existing, ...prices };
        localStorage.setItem(LAST_PRICES_KEY, JSON.stringify(merged));
    } catch { /* ignore */ }
};

/**
 * Reconciles localStorage spot holdings against the backend's open spot trades.
 * This is the single source of truth reconciliation — it handles:
 *   1. Admin-opened trades appearing in the user's portfolio
 *   2. Correct holdings after sells (sell trades are closed immediately by backend)
 *
 * Call this after every getMyOpenTrades() response. It is idempotent.
 *
 * Logic: Only open buy trades represent holdings. Sells are created as 'closed'
 * by the backend, so they never appear in getMyOpenTrades().
 */
export const syncSpotHoldingsFromBackend = (openTrades: any[]): void => {
    try {
        const buyMap: Record<string, number> = {};
        const touchedCoins = new Set<string>();

        for (const trade of openTrades) {
            if (trade.type !== 'spot' || trade.status !== 'open') continue;
            if (trade.side !== 'buy' && trade.side !== 'long') continue;
            const sym = (trade.asset as string).split('/')[0].toUpperCase();
            touchedCoins.add(sym);
            buyMap[sym] = (buyMap[sym] || 0) + (Number(trade.amount) || 0);
        }

        const holdings = getSpotHoldings();
        let changed = false;

        // Zero out any coin that was in holdings but is no longer in open trades
        for (const sym of Object.keys(holdings)) {
            if (!touchedCoins.has(sym) && (holdings[sym] || 0) > 0) {
                holdings[sym] = 0;
                changed = true;
                // Also clear cost basis for this coin
                try {
                    const cbKey = userKey('dw_cost_basis');
                    const cb = JSON.parse(localStorage.getItem(cbKey) || '{}');
                    if (cb[sym]) { delete cb[sym]; localStorage.setItem(cbKey, JSON.stringify(cb)); }
                } catch { /* ignore */ }
            }
        }

        for (const sym of touchedCoins) {
            const corrected = parseFloat((buyMap[sym] || 0).toFixed(8));
            if ((holdings[sym] || 0) !== corrected) {
                holdings[sym] = corrected;
                changed = true;
            }
        }

        if (changed) saveSpotHoldings(holdings);

        // Ensure timestamps exist for any newly synced coins (used for sort order)
        const tsKey = userKey('dw_spot_timestamps');
        const timestamps = JSON.parse(localStorage.getItem(tsKey) || '{}');
        let tsDirty = false;
        for (const sym of touchedCoins) {
            if (!timestamps[sym] && (holdings[sym] || 0) > 0) {
                timestamps[sym] = Date.now();
                tsDirty = true;
            }
        }
        if (tsDirty) localStorage.setItem(tsKey, JSON.stringify(timestamps));
    } catch { /* ignore */ }
};

export const deductSpotHolding = (coin: string, amount: number): void => {
    const holdings = getSpotHoldings();
    const key = coin.toUpperCase();
    const prevHeld = holdings[key] || 0;
    holdings[key] = Math.max(0, parseFloat((prevHeld - amount).toFixed(8)));
    saveSpotHoldings(holdings);
    // Proportionally reduce cost basis so the invested amount stays accurate
    try {
        const costKey = userKey('dw_cost_basis');
        const existing = JSON.parse(localStorage.getItem(costKey) || '{}');
        if (existing[key] && prevHeld > 0) {
            const ratio = Math.min(amount / prevHeld, 1);
            existing[key] = Math.max(0, parseFloat((existing[key] * (1 - ratio)).toFixed(4)));
            localStorage.setItem(costKey, JSON.stringify(existing));
        }
    } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Futures balance – stored separately from spot balance
// ---------------------------------------------------------------------------


export const getFuturesBalance = (): number => {
    try { return parseFloat(localStorage.getItem(userKey('dw_futures_balance')) || '0') || 0; } catch { return 0; }
};

export const setFuturesBalance = (balance: number): void => {
    try { localStorage.setItem(userKey('dw_futures_balance'), balance.toFixed(8)); } catch { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Internal spot ↔ futures transfers
// ---------------------------------------------------------------------------
export const transferSpotToFutures = (amount: number): void => {
    const spot = getCachedBalance();
    const futures = getFuturesBalance();
    setCachedBalance(Math.max(0, parseFloat((spot - amount).toFixed(8))));
    setFuturesBalance(parseFloat((futures + amount).toFixed(8)));
    // Also record as a local balance change so getBalance() stays in sync
    applyLocalBalanceChange(-amount);
};

export const transferFuturesToSpot = (amount: number): void => {
    const spot = getCachedBalance();
    const futures = getFuturesBalance();
    setFuturesBalance(Math.max(0, parseFloat((futures - amount).toFixed(8))));
    setCachedBalance(parseFloat((spot + amount).toFixed(8)));
    applyLocalBalanceChange(amount);
};
// ---------------------------------------------------------------------------


export const getBalance = async (): Promise<{ balance: number; futuresBalance?: number; stale?: boolean }> => {
    try {
        const response = await authFetch(`${API_URL}/balance`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || 'Failed to fetch balance');
        }
        
        // Ensure the response is actually JSON before parsing (to avoid HTML "Unexpected token '<'" errors)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            throw new Error(`Expected JSON but got ${contentType}. Check your VITE_API_BASE_URL.`);
        }
        
        const data = await response.json();
        const backendBal = data.balance || 0;
        // Sync futures balance from DB (authoritative source).
        if (typeof data.futuresBalance === 'number') {
            setFuturesBalance(data.futuresBalance);
        }
        // Backend is the source of truth — reset any stale local adjustments
        // and cache the backend value directly. Local adjustments were a workaround
        // for optimistic UI but caused balance drift across sessions.
        resetLocalBalanceAdjustment();
        setCachedBalance(backendBal);
        return { ...data, balance: backendBal };
    } catch (err: any) {
        // Backend unreachable — use cached balance and flag as stale
        const cached = getCachedBalance();
        if (cached > 0) return { balance: cached, futuresBalance: getFuturesBalance(), stale: true };
        throw err;
    }
};

export const getAddress = async (asset: string = 'USDT', network?: string): Promise<{ address: string }> => {
    const query = network ? `?asset=${asset}&network=${network}` : `?asset=${asset}`;
    const response = await authFetch(`${API_URL}/address${query}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to fetch address');
    }    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Expected JSON but got ${contentType}. Check your VITE_API_BASE_URL.`);
    }    return response.json();
};

export const getTransactions = async (): Promise<any[]> => {
    const response = await authFetch(`${API_URL}/transactions`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to fetch transactions');
    }
    return response.json();
};

// Direct deposit — enter amount, balance credited immediately
export const directDeposit = async (amount: number, asset: string): Promise<{ balance: number; transaction: any }> => {
    const response = await authFetch(`${API_URL}/direct-deposit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount, asset }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Deposit failed');
    }
    return response.json();
};

// Notify admin of a manual deposit (creates pending record — admin approves to credit balance)
export const submitDepositNotification = async (amount: number, asset: string, network: string, txHash?: string, depositRef?: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/deposit-notify`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount, asset, network, txHash, depositRef }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to submit deposit notification');
    }
    return response.json();
};

export const simulateDeposit = async (amount: number, asset: string = 'USDT'): Promise<any> => {
    const response = await authFetch(`${API_URL}/deposit-simulator`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount, asset }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to simulate deposit');
    }
    return response.json();
};

// Create a real crypto deposit via Cryptomus
export const createDeposit = async (amount: number, currency: string = 'USDT', network?: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/create-deposit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount, currency, network }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create deposit');
    }
    return response.json();
};

// Check deposit status
export const getDepositStatus = async (transactionId: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/deposit-status/${transactionId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to check deposit status');
    }
    return response.json();
};

export const getWithdrawalFee = async (asset?: string, network?: string): Promise<{ fee: number; feeType?: string; minWithdrawal?: number }> => {
    const params = new URLSearchParams();
    if (asset) params.set('asset', asset);
    if (network) params.set('network', network);
    const qs = params.toString();
    const response = await authFetch(`${API_URL}/fee${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to fetch withdrawal fee');
    }
    return response.json();
};

/** Returns which withdrawal networks currently have sufficient hot-wallet gas balance,
 *  e.g. { BEP20: true, TRC20: true, ERC20: false } — used to grey out unavailable
 *  networks on the withdraw page before the user even attempts a withdrawal. */
export const getNetworkAvailability = async (): Promise<Record<string, boolean>> => {
    try {
        const response = await authFetch(`${API_URL}/network-availability`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        if (!response.ok) return {};
        const data = await response.json();
        return data.data || {};
    } catch {
        // Fail open — don't block the withdraw page if this check itself fails
        return {};
    }
};

/** On-demand re-check of a withdrawal's live status against Plisio. Call when the
 *  user opens withdrawal details, since Plisio doesn't push a webhook when a
 *  withdrawal completes on-chain (only deposits get that) — only polling reveals it. */
export const checkWithdrawalStatus = async (transactionId: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/withdrawal-status/${transactionId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to check withdrawal status');
    }
    const data = await response.json();
    return data.transaction;
};

export const sendWithdrawalOTP = async (): Promise<{ message: string }> => {
    const response = await authFetch(`${API_URL}/withdraw/send-otp`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to send OTP');
    }
    return response.json();
};

export const withdrawFunds = async (
    amount: number, 
    address: string, 
    asset: string = 'USDT', 
    network: string = 'BNB Smart Chain (BEP20)', 
    fundPassword?: string, 
    google2faCode?: string,
    emailOtpCode?: string
): Promise<any> => {
    const response = await authFetch(`${API_URL}/withdraw`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount, address, asset, network, fundPassword, google2faCode, emailOtpCode }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to withdraw funds');
    }
    return response.json();
};

// Internal Spot ↔ Futures transfer — validates via backend then syncs localStorage.
// Throws on failure (e.g. insufficient balance) so callers can show the error.
export const recordInternalTransfer = async (amount: number, fromAccount: string, toAccount: string, asset: string = 'USDT'): Promise<{ balance: number; futuresBalance: number }> => {
    const response = await authFetch(`${API_URL}/record-transfer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            amount,
            fromAccount: fromAccount.toLowerCase(),
            toAccount: toAccount.toLowerCase(),
            asset,
        }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Transfer failed');
    }
    const data = await response.json();
    // Sync localStorage from the authoritative DB values.
    if (typeof data.balance === 'number') {
        setCachedBalance(data.balance);
        resetLocalBalanceAdjustment();
    }
    if (typeof data.futuresBalance === 'number') {
        setFuturesBalance(data.futuresBalance);
    }
    return {
        balance: data.balance || 0,
        futuresBalance: data.futuresBalance || 0,
    };
};

export const transferFunds = async (
    recipientId: string, 
    amount: number, 
    asset: string = 'USDT', 
    network: string = 'Internal Ledger',
    fundPassword?: string,
    google2faCode?: string,
    emailOtpCode?: string
): Promise<any> => {
    const response = await authFetch(`${API_URL}/transfer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ recipientId, amount, asset, network, fundPassword, google2faCode, emailOtpCode }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to transfer funds');
    }
    // Real backend success — record deduction too so local state stays in sync
    applyLocalBalanceChange(-amount);
    return response.json();
};
